import React, { useState, useEffect } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import CloseIcon from '@mui/icons-material/Close';
import SnackbarManager from './SnackbarManager';
import './GlobalSnackbar.scss';

export default function GlobalSnackbar() {
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        type: 'info',
        duration: 5000
    });
    const [timeout, setTimeoutId] = useState(null);

    useEffect(() => {
        const unsubscribe = SnackbarManager.subscribe((data) => {
            setSnackbar(data);
        });

        return unsubscribe;
    }, []);

    useEffect(() => {
        if (snackbar.open) {
            // Clear any existing timeout
            if (timeout) {
                clearTimeout(timeout);
            }
            
            // Set new timeout
            const id = setTimeout(() => {
                handleClose();
            }, snackbar.duration);
            
            setTimeoutId(id);
        }
        
        return () => {
            if (timeout) {
                clearTimeout(timeout);
            }
        };
    }, [snackbar.open, snackbar.duration]);

    const handleClose = () => {
        const element = document.querySelector('.globalSnackbar');
        if (element) {
            element.classList.add('globalSnackbar--exit');
            setTimeout(() => {
                setSnackbar(prev => ({ ...prev, open: false }));
                if (timeout) {
                    clearTimeout(timeout);
                    setTimeoutId(null);
                }
            }, 300);
        } else {
            setSnackbar(prev => ({ ...prev, open: false }));
            if (timeout) {
                clearTimeout(timeout);
                setTimeoutId(null);
            }
        }
    };

    const handleMouseEnter = () => {
        if (timeout) {
            clearTimeout(timeout);
            setTimeoutId(null);
        }
    };

    const handleMouseLeave = () => {
        if (snackbar.open) {
            const id = setTimeout(() => {
                handleClose();
            }, 2000);
            setTimeoutId(id);
        }
    };

    const getIcon = () => {
        switch (snackbar.type) {
            case 'success':
                return <CheckCircleIcon className="globalSnackbar__icon" />;
            case 'error':
                return <ErrorIcon className="globalSnackbar__icon" />;
            case 'info':
                return <InfoIcon className="globalSnackbar__icon" />;
            case 'warning':
                return <WarningIcon className="globalSnackbar__icon" />;
            default:
                return <InfoIcon className="globalSnackbar__icon" />;
        }
    };

    const getTitle = () => {
        switch (snackbar.type) {
            case 'success':
                return 'Success';
            case 'error':
                return 'Error';
            case 'info':
                return 'Information';
            case 'warning':
                return 'Warning';
            default:
                return 'Notification';
        }
    };

    if (!snackbar.open) return null;

    return (
        <div className="globalSnackbar__container">
            <div 
                className={`globalSnackbar globalSnackbar--${snackbar.type}`}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <div className="globalSnackbar__content">
                    <div className="globalSnackbar__iconContainer">
                        {getIcon()}
                    </div>
                    <div className="globalSnackbar__messageContainer">
                        <div className="globalSnackbar__title">
                            {getTitle()}
                        </div>
                        <div className="globalSnackbar__message">
                            {snackbar.message}
                        </div>
                    </div>
                    <button 
                        className="globalSnackbar__closeBtn"
                        onClick={handleClose}
                    >
                        <CloseIcon />
                    </button>
                </div>
                <div className="globalSnackbar__progressBar">
                    <div 
                        className="globalSnackbar__progress"
                        style={{ animationDuration: `${snackbar.duration}ms` }}
                    ></div>
                </div>
            </div>
        </div>
    );
}